import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Icon, Button, toast } from '@tviklax/ui-components';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import type { OrderItem } from './types';
import { getStatesForCountry } from './addressData';

const ImportContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  padding: ${({ theme }) => theme.spacing[8]};
  border: 2px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.gray100};
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}05;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[4]};
  }
`;

const UploadIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatusOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  z-index: 10;
`;

const ProgressTrack = styled.div`
  width: 240px;
  height: 6px;
  background: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressBar = styled(motion.div)`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
`;

interface ImportInvoiceProps {
    onDataExtracted: (data: any, file: File) => void;
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type ValidationResponse = {
    is_valid_format: boolean;
    validation_score: number;
    warnings: string[];
    needs_manual_review?: boolean;
    manual_review_reasons?: string[];
    extracted_data?: {
        document_name?: string | null;
        invoice_number?: string | null;
        invoice_date?: string | null;
        due_date?: string | null;
        currency?: string | null;
        total_amount?: number | null;
        from_name?: string | null;
        from_address?: string | null;
        from_street?: string | null;
        from_city?: string | null;
        from_state?: string | null;
        from_postal_code?: string | null;
        from_country?: string | null;
        from_phone?: string | null;
        from_email?: string | null;
        to_name?: string | null;
        to_address?: string | null;
        to_street?: string | null;
        to_city?: string | null;
        to_state?: string | null;
        to_postal_code?: string | null;
        to_country?: string | null;
        to_phone?: string | null;
        to_email?: string | null;
        line_items?: Array<{
            description?: string | null;
            quantity?: number | null;
            unit?: string | null;
            unit_price?: number | null;
            line_total?: number | null;
        }>;
    };
    field_confidence?: Record<string, number>;
};

export type ImportStatus = 'idle' | 'uploading' | 'extracting' | 'completed' | 'failed';

const parseAddress = (address?: string | null) => {
    if (!address) {
        return { line1: '', city: '', state: '', country: '', zip: '' };
    }
    const compact = address.replace(/\n+/g, ', ').replace(/\s+/g, ' ').trim();
    const parts = compact.split(',').map(p => p.trim()).filter(Boolean);
    const line1 = parts.slice(0, Math.max(1, parts.length - 3)).join(', ') || compact;
    const city = parts.length >= 3 ? parts[parts.length - 3] : '';
    const state = parts.length >= 2 ? parts[parts.length - 2] : '';
    const country = parts.length >= 1 ? parts[parts.length - 1] : '';
    const zipMatch = compact.match(/\b\d{4,6}\b/);
    return { line1, city, state, country, zip: zipMatch?.[0] || '' };
};

const firstLine = (value?: string | null) => {
    if (!value) return '';
    return value.split(/\n|,/).map(part => part.trim()).find(Boolean) || '';
};

// The Country <Select> in the manual-entry forms only recognizes 2-letter
// codes ('US'/'IN'/'PL'). Extraction (LLM or regex) can return a full
// country name instead despite the prompt asking for a code — when that
// happens the value matches no <option>, and since the placeholder option
// is disabled, browsers fall back to selecting the first *enabled* option
// in the list ("United States") instead of showing nothing, which silently
// mis-assigns the country (and cascades into the State dropdown resolving
// to an empty, disabled option list). Normalize defensively here so a
// non-code value never reaches form state.
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
    'us': 'US', 'usa': 'US', 'u.s.a.': 'US', 'u.s.a': 'US', 'united states': 'US', 'united states of america': 'US',
    'in': 'IN', 'india': 'IN',
    'pl': 'PL', 'poland': 'PL',
};

const normalizeCountryCode = (value?: string | null): string => {
    if (!value) return '';
    const key = value.trim().toLowerCase();
    return COUNTRY_NAME_TO_CODE[key] || (value.trim().length === 2 ? value.trim().toUpperCase() : '');
};

// Same problem as country, one level down: the State <Select> only
// recognizes the 2-letter subdivision codes from the `states` table (e.g.
// 'TN', 'CA') as values, but extraction can still return the full name
// (e.g. "Tamilnadu") despite the prompt asking for a code.
const normalizeStateCode = async (countryCode: string, value?: string | null): Promise<string> => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!countryCode) return trimmed;
    const options = await getStatesForCountry(countryCode);
    if (options.length === 0) return trimmed; // country with no fixed state list (e.g. Poland) — keep as free text
    const byName = options.find(s => s.label.toLowerCase() === trimmed.toLowerCase());
    if (byName) return byName.value;
    // Already a valid code (case-insensitive) for this country?
    const upper = trimmed.toUpperCase();
    const isKnownCode = options.some(s => s.value === upper);
    return isKnownCode ? upper : '';
};

const mapLineItems = (response: ValidationResponse): OrderItem[] => {
    const extractedItems = response.extracted_data?.line_items || [];
    return extractedItems.map((item, index) => {
        const quantity = Math.max(0, Number(item.quantity || 0));
        const unitPrice = Number(item.unit_price || 0);
        const lineTotal = Number(item.line_total || quantity * unitPrice || 0);
        const description = (item.description || '').trim();

        return {
            itemno: index + 1,
            productId: '',
            productName: description || `Item ${index + 1}`,
            details: description,
            unit: item.unit || 'pcs',
            quantity,
            unitPrice,
            price: unitPrice,
            total: lineTotal,
            subtotal: lineTotal,
            countryOfOrigin: '',
            exportControlFlag: '',
            ecgcEligibility: 'Pending check',
        };
    });
};

const mapValidationToOrderImportData = async (response: ValidationResponse) => {
    const extracted = response.extracted_data || {};
    // Prefer structured address components from the LLM extraction path — it
    // reads the document and identifies city/state/postal code/country
    // directly, which is far more reliable than splitting a flat address
    // string by commas afterward (the parseAddress() fallback below, only
    // used when the LLM path wasn't available and we're on the regex path).
    const hasStructuredFromAddr = Boolean(extracted.from_city || extracted.from_state || extracted.from_country);
    const hasStructuredToAddr = Boolean(extracted.to_city || extracted.to_state || extracted.to_country);

    const fromCountry = hasStructuredFromAddr
        ? normalizeCountryCode(extracted.from_country)
        : normalizeCountryCode(parseAddress(extracted.from_address).country);
    const toCountry = hasStructuredToAddr
        ? normalizeCountryCode(extracted.to_country)
        : normalizeCountryCode(parseAddress(extracted.to_address).country);

    const [fromState, toState] = await Promise.all([
        normalizeStateCode(fromCountry, hasStructuredFromAddr ? extracted.from_state : parseAddress(extracted.from_address).state),
        normalizeStateCode(toCountry, hasStructuredToAddr ? extracted.to_state : parseAddress(extracted.to_address).state),
    ]);

    const fromAddr = hasStructuredFromAddr
        ? {
            line1: extracted.from_street || firstLine(extracted.from_address),
            city: extracted.from_city || '',
            state: fromState,
            country: fromCountry,
            zip: extracted.from_postal_code || '',
        }
        : { ...parseAddress(extracted.from_address), state: fromState, country: fromCountry };
    const toAddr = hasStructuredToAddr
        ? {
            line1: extracted.to_street || firstLine(extracted.to_address),
            city: extracted.to_city || '',
            state: toState,
            country: toCountry,
            zip: extracted.to_postal_code || '',
        }
        : { ...parseAddress(extracted.to_address), state: toState, country: toCountry };
    const customerName =
        extracted.to_name ||
        firstLine(extracted.to_address) ||
        extracted.to_email ||
        'Imported Customer';
    const vendorName =
        extracted.from_name ||
        firstLine(extracted.from_address) ||
        extracted.from_email ||
        'Imported Vendor';
    const items = mapLineItems(response);
    const itemsSubtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const resolvedSubtotal = extracted.total_amount || itemsSubtotal || 0;

    const customerAddr1 = toAddr.line1 || firstLine(extracted.to_address);
    const vendorAddr1 = fromAddr.line1 || firstLine(extracted.from_address);

    return {
        CustomerName: customerName || '',
        CustomerEmail: extracted.to_email || '',
        CustomerContactNumber: extracted.to_phone || '',
        customerAddr1: customerAddr1 || '',
        customerCity: toAddr.city,
        customerState: toAddr.state,
        customerCountry: toAddr.country,
        customerZip: toAddr.zip,
        VendorName: vendorName || '',
        VendorEmail: extracted.from_email || '',
        VendorContactNumber: extracted.from_phone || '',
        vendorAddr1: vendorAddr1 || '',
        vendorCity: fromAddr.city,
        vendorState: fromAddr.state,
        vendorCountry: fromAddr.country,
        vendorZip: fromAddr.zip,
        items,
        currency: extracted.currency || 'USD',
        invoiceNumber: extracted.invoice_number || '',
        deliveryDate: extracted.due_date || extracted.invoice_date || '',
        subtotal: resolvedSubtotal,
        total: resolvedSubtotal,
        taxAmount: 0,
        discountAmount: 0,
        confidence: {
            CustomerName: response.field_confidence?.to_address ?? 0,
            VendorName: response.field_confidence?.from_address ?? 0,
            Address: Math.max(response.field_confidence?.to_address ?? 0, response.field_confidence?.from_address ?? 0),
        },
        validation: {
            isValidFormat: response.is_valid_format,
            validationScore: response.validation_score,
            needsManualReview: response.needs_manual_review ?? false,
            manualReviewReasons: response.manual_review_reasons || [],
            warnings: response.warnings || [],
        },
    };
};

export const ImportInvoice: React.FC<ImportInvoiceProps> = ({ onDataExtracted }) => {
    const [status, setStatus] = useState<ImportStatus>('idle');
    const [progress, setProgress] = useState(0);

    const processInvoiceUpload = useCallback(async (file: File) => {
        try {
            setStatus('uploading');
            setProgress(20);

            const formData = new FormData();
            formData.append('file', file);

            setStatus('extracting');
            setProgress(70);

            const response = await fetch('http://localhost:9001/api/v1/forex/validate-proforma-invoice/upload', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json().catch(() => ({ detail: 'Invoice validation failed' }));
                throw new Error(error.detail || 'Invoice validation failed');
            }

            const validationResult = (await response.json()) as ValidationResponse;
            const mappedData = await mapValidationToOrderImportData(validationResult);

            setProgress(100);
            setStatus('completed');

            if (validationResult.needs_manual_review) {
                toast({
                    title: 'Invoice needs manual review',
                    description: `Score: ${validationResult.validation_score.toFixed(0)}%. Please review flagged fields before confirming.`,
                });
            } else if (!validationResult.is_valid_format) {
                toast({
                    title: 'Invoice format issues found',
                    description: `Score: ${validationResult.validation_score.toFixed(0)}%. You can still edit manually.`,
                    variant: 'destructive',
                });
            } else {
                toast({
                    title: 'Invoice extracted successfully',
                    description: `Validation score: ${validationResult.validation_score.toFixed(0)}%`,
                });
            }

            onDataExtracted(mappedData, file);
        } catch (error) {
            setStatus('failed');
            setProgress(0);
            toast({
                title: 'Invoice upload failed',
                description: (error as Error).message || 'Unable to process invoice.',
                variant: 'destructive',
            });
            setTimeout(() => setStatus('idle'), 1200);
        }
    }, [onDataExtracted]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            void processInvoiceUpload(acceptedFiles[0]);
        }
    }, [processInvoiceUpload]);

    const onDropRejected = useCallback((rejections: any[]) => {
        const rejection = rejections[0];
        const isTooLarge = rejection?.errors?.some((e: any) => e.code === 'file-too-large');
        toast({
            title: isTooLarge ? 'File too large' : 'Unsupported file type',
            description: isTooLarge
                ? 'Please upload a file smaller than 10MB.'
                : 'Only PDF, JPEG, and PNG files are supported.',
            variant: 'destructive',
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: {
            'application/pdf': ['.pdf'],
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
        },
        maxSize: MAX_FILE_SIZE_BYTES,
        multiple: false,
        disabled: status !== 'idle'
    });

    return (
        <div style={{ position: 'relative' }}>
            <AnimatePresence>
                {status !== 'idle' && (
                    <StatusOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <Icon
                            name={status === 'completed' ? 'CheckCircle' : 'Zap'}
                            size={48}
                            color={status === 'completed' ? '#10b981' : '#f86400'}
                        />
                        <p style={{ fontWeight: 600, color: '#1e293b' }}>
                            {status === 'uploading' && 'Uploading document...'}
                            {status === 'extracting' && 'Parsing document structure via Tvikla Parser OCR...'}
                            {status === 'completed' && 'Data successfully extracted!'}
                            {status === 'failed' && 'Failed to process invoice'}
                        </p>
                        <ProgressTrack>
                            <ProgressBar
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 0.5 }}
                            />
                        </ProgressTrack>
                    </StatusOverlay>
                )}
            </AnimatePresence>

            <ImportContainer {...getRootProps()}>
                <input {...getInputProps()} />
                <UploadIconWrapper>
                    <Icon name="Upload" size={32} />
                </UploadIconWrapper>
                <div style={{ textAlign: 'center' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1e293b' }}>
                        {isDragActive ? 'Drop the file here' : 'Upload Invoice or Document'}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '4px' }}>
                        Upload a Purchase Order or Proforma Invoice PDF
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '2px' }}>
                        PDF, JPEG, or PNG — up to 10MB
                    </p>
                </div>
                <Button variant="outline" size="sm" type="button">
                    Select File
                </Button>
            </ImportContainer>
        </div>
    );
};
